import React, { useState, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import UnitPageShell from '../../components/shared/UnitPageShell';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { tokens } from '../../theme/tokens';

type SnackbarState = { message: string; severity: 'success' | 'info' | 'error' } | null;

const DIP_VALUES = [1, 2, 4, 8, 16, 32, 64, 128];

// SVG Component: Single DIP Switch
const DipSwitchSVG: React.FC<{
  index: number;
  isOn: boolean;
  value: number;
  interactive?: boolean;
  onToggle?: () => void;
  darkMode: boolean;
}> = ({ index, isOn, value, interactive = false, onToggle, darkMode }) => {
  const switchColor = isOn ? '#1B873B' : '#B2140F';
  const baseColor = darkMode ? '#2A2A2A' : '#1A1A1A';
  const labelColor = darkMode ? '#E0E0E0' : '#333333';
  
  return (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 100 140"
      style={{ cursor: interactive ? 'pointer' : 'default' }}
      onClick={interactive ? onToggle : undefined}
    >
      {/* Base/Housing */}
      <rect x="10" y="10" width="80" height="120" rx="4" fill={baseColor} stroke="#555" strokeWidth="1" />
      <rect x="15" y="15" width="70" height="110" rx="2" fill="#2C2C2C" />
      
      {/* Switch Slot */}
      <rect x="35" y="30" width="30" height="70" rx="2" fill="#1A1A1A" stroke="#000" strokeWidth="0.5" />
      
      {/* Switch Toggle (animated position) */}
      <g style={{ transition: 'transform 0.2s ease-in-out' }} transform={isOn ? 'translate(0, 0)' : 'translate(0, 30)'}>
        <rect x="37" y="40" width="26" height="30" rx="3" fill={switchColor} stroke="#000" strokeWidth="1" />
        <rect x="39" y="42" width="22" height="26" rx="2" fill={isOn ? '#22A84A' : '#D41610'} />
        <line x1="42" y1="55" x2="58" y2="55" stroke="#000" strokeWidth="1.5" strokeLinecap="round" />
      </g>
      
      {/* Labels */}
      <text x="20" y="25" fontSize="10" fill={labelColor} fontWeight="bold">ON</text>
      <text x="20" y="115" fontSize="10" fill={labelColor} fontWeight="bold">OFF</text>
      
      {/* Switch Number */}
      <text x="50" y="135" fontSize="12" fill={labelColor} textAnchor="middle" fontWeight="bold">
        {index + 1}
      </text>
      
      {/* Value */}
      <text x="85" y="70" fontSize="10" fill={switchColor} fontWeight="bold" textAnchor="start">
        {value}
      </text>
    </svg>
  );
};

// SVG Component: Potentiometer
const PotentiometerSVG: React.FC<{
  label: string;
  value: number;
  max: number;
  onChange: (value: number) => void;
  darkMode: boolean;
}> = ({ label, value, max, onChange, darkMode }) => {
  const angle = (value / max) * 270 - 135; // -135° to +135°
  const bodyColor = darkMode ? '#2A2A2A' : '#1A1A1A';
  const labelColor = darkMode ? '#E0E0E0' : '#333333';
  const accentColor = '#1976d2';

  return (
    <Box sx={{ textAlign: 'center' }}>
      <svg width="100%" height="100%" viewBox="0 0 200 200" style={{ maxWidth: 200, margin: '0 auto' }}>
        {/* Outer Ring/Scale */}
        <circle cx="100" cy="100" r="85" fill="none" stroke={darkMode ? '#444' : '#DDD'} strokeWidth="8" />
        
        {/* Scale Marks */}
        {Array.from({ length: max + 1 }).map((_, i) => {
          const markAngle = (i / max) * 270 - 135;
          const radians = (markAngle * Math.PI) / 180;
          const x1 = 100 + 75 * Math.cos(radians);
          const y1 = 100 + 75 * Math.sin(radians);
          const x2 = 100 + (i % 5 === 0 ? 65 : 70) * Math.cos(radians);
          const y2 = 100 + (i % 5 === 0 ? 65 : 70) * Math.sin(radians);
          
          return (
            <React.Fragment key={i}>
              <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={labelColor} strokeWidth={i % 5 === 0 ? 2 : 1} />
              {i % 5 === 0 && (
                <text
                  x={100 + 55 * Math.cos(radians)}
                  y={100 + 55 * Math.sin(radians) + 4}
                  fontSize="10"
                  fill={labelColor}
                  textAnchor="middle"
                  fontWeight="bold"
                >
                  {i}
                </text>
              )}
            </React.Fragment>
          );
        })}
        
        {/* Body */}
        <circle cx="100" cy="100" r="60" fill={bodyColor} stroke="#555" strokeWidth="2" />
        <circle cx="100" cy="100" r="55" fill="#2C2C2C" />
        
        {/* Center Cap */}
        <circle cx="100" cy="100" r="45" fill={darkMode ? '#1A1A1A' : '#3A3A3A'} stroke="#000" strokeWidth="1" />
        
        {/* Pointer (rotates with value) */}
        <g transform={`rotate(${angle} 100 100)`}>
          <line x1="100" y1="100" x2="100" y2="55" stroke={accentColor} strokeWidth="4" strokeLinecap="round" />
          <circle cx="100" cy="55" r="5" fill={accentColor} />
        </g>
        
        {/* Center Screw */}
        <circle cx="100" cy="100" r="8" fill="#666" stroke="#444" strokeWidth="1" />
        <line x1="95" y1="100" x2="105" y2="100" stroke="#333" strokeWidth="1.5" />
      </svg>
      
      <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 600, color: labelColor }}>
        {label}
      </Typography>
      
      <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mt: 1 }}>
        <Button
          size="small"
          variant="outlined"
          onClick={() => onChange(Math.max(0, value - 1))}
          disabled={value === 0}
          sx={{ minWidth: 40 }}
        >
          -
        </Button>
        <TextField
          value={value}
          onChange={(e) => {
            const num = parseInt(e.target.value) || 0;
            onChange(Math.max(0, Math.min(max, num)));
          }}
          type="number"
          size="small"
          inputProps={{ min: 0, max, style: { textAlign: 'center', width: 60 } }}
        />
        <Button
          size="small"
          variant="outlined"
          onClick={() => onChange(Math.min(max, value + 1))}
          disabled={value === max}
          sx={{ minWidth: 40 }}
        >
          +
        </Button>
      </Stack>
    </Box>
  );
};

const Page: React.FC = () => {
  const { darkMode } = useTheme();
  const { canViewDipschalter } = useAuth();

  // Mode 1: Address → DIP Switches
  const [addressInput, setAddressInput] = useState<string>('');
  
  // Mode 2: DIP Switches → Address
  const [switchStates, setSwitchStates] = useState<boolean[]>(DIP_VALUES.map(() => false));
  
  // Mode 3: Potentiometers
  const [poti1, setPoti1] = useState<number>(0); // 0-15 (Einer)
  const [poti2, setPoti2] = useState<number>(0); // 0-15 (Sechzehner)
  const [potiAddressInput, setPotiAddressInput] = useState<string>(''); // Address input for potis
  
  const [snackbar, setSnackbar] = useState<SnackbarState>(null);

  // Calculate DIP states from address input
  const { dipStates, addressError } = useMemo(() => {
    const trimmed = addressInput.trim();
    if (!trimmed) {
      return { dipStates: DIP_VALUES.map(() => false), addressError: null };
    }

    if (!/^\d+$/.test(trimmed)) {
      return { dipStates: DIP_VALUES.map(() => false), addressError: 'Nur Ziffern zulässig.' };
    }

    const numeric = Number(trimmed);
    if (!Number.isFinite(numeric) || numeric < 1 || numeric > 255) {
      return { dipStates: DIP_VALUES.map(() => false), addressError: 'Adresse muss zwischen 1 und 255 liegen.' };
    }

    let remaining = Math.floor(numeric);
    const states = DIP_VALUES.map(() => false);
    for (let index = DIP_VALUES.length - 1; index >= 0; index -= 1) {
      const value = DIP_VALUES[index];
      if (remaining >= value) {
        states[index] = true;
        remaining -= value;
      }
    }

    return { dipStates: states, addressError: null };
  }, [addressInput]);

  // Calculate address from switch states
  const calculatedAddress = useMemo(
    () => switchStates.reduce((sum, isOn, index) => (isOn ? sum + DIP_VALUES[index] : sum), 0),
    [switchStates]
  );

  // Calculate address from potentiometers
  const potiAddress = useMemo(() => poti2 * 16 + poti1, [poti1, poti2]);

  // Calculate binary representation
  const toBinary = (num: number) => num.toString(2).padStart(8, '0');

  const handleSwitchToggle = (index: number) => {
    setSwitchStates(prev => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  };

  const resetSwitches = () => {
    setSwitchStates(DIP_VALUES.map(() => false));
    setSnackbar({ message: 'Alle Schalter zurückgesetzt.', severity: 'info' });
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setSnackbar({ message: `${label} kopiert: ${text}`, severity: 'success' });
    });
  };

  // Handler for potentiometer address input
  const handlePotiAddressChange = (value: string) => {
    setPotiAddressInput(value);
    const trimmed = value.trim();
    if (trimmed && /^\d+$/.test(trimmed)) {
      const num = parseInt(trimmed);
      if (num >= 0 && num <= 255) {
        setPoti2(Math.floor(num / 16));
        setPoti1(num % 16);
      }
    }
  };

  const actionButtons = (
    <Tooltip title="Alle zurücksetzen">
      <IconButton
        color="inherit"
        onClick={() => {
          setAddressInput('');
          setSwitchStates(DIP_VALUES.map(() => false));
          setPoti1(0);
          setPoti2(0);
          setPotiAddressInput('');
          setSnackbar({ message: 'Alle Werte zurückgesetzt.', severity: 'info' });
        }}
      >
        <RefreshIcon />
      </IconButton>
    </Tooltip>
  );

  if (!canViewDipschalter) {
    return (
      <UnitPageShell title="M99 · Dip-Schalter & Adress-Rechner" showTTS={false}>
        <Alert severity="error">Keine Berechtigung fuer den DIP-Schalter Rechner.</Alert>
      </UnitPageShell>
    );
  }

  return (
    <UnitPageShell
      title="M99 · Dip-Schalter & Adress-Rechner"
      showTTS={false}
      actions={actionButtons}
    >
      <Stack spacing={3}>
        {/* Mode 1: Address → DIP Switches */}
        <Card elevation={2}>
          <CardContent>
            <Stack spacing={3}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: tokens.primary }}>
                1. Adresse → Dip-Schalter
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                <TextField
                  fullWidth
                  label="DMX/BACnet Adresse (1-255)"
                  value={addressInput}
                  onChange={(e) => setAddressInput(e.target.value)}
                  error={Boolean(addressError)}
                  helperText={addressError || 'Gib die Adresse ein, um die DIP-Schalter Einstellungen zu sehen.'}
                  inputProps={{ maxLength: 3, inputMode: 'numeric' }}
                />
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      const num = parseInt(addressInput) || 0;
                      setAddressInput(String(Math.max(1, num - 1)));
                    }}
                    sx={{ minWidth: 80 }}
                  >
                    -1
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      const num = parseInt(addressInput) || 0;
                      setAddressInput(String(Math.min(255, num + 1)));
                    }}
                    sx={{ minWidth: 80 }}
                  >
                    +1
                  </Button>
                </Stack>
              </Stack>

              {addressInput.trim() && !addressError && (
                <>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: 'repeat(4, 1fr)',
                        sm: 'repeat(8, 1fr)'
                      },
                      gap: { xs: 1, sm: 2 },
                      maxWidth: 800,
                      mx: 'auto'
                    }}
                  >
                    {DIP_VALUES.map((value, index) => (
                      <Box key={value} sx={{ aspectRatio: '1/1.4' }}>
                        <DipSwitchSVG
                          index={index}
                          isOn={dipStates[index]}
                          value={value}
                          darkMode={darkMode}
                        />
                      </Box>
                    ))}
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 2,
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 2,
                      bgcolor: tokens.primary + '15',
                      borderRadius: 2
                    }}
                  >
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      Binär:
                    </Typography>
                    <Typography variant="h6" sx={{ fontFamily: 'monospace', color: (darkMode ? '#22A84A' : '#1B873B') }}>
                      {toBinary(parseInt(addressInput))}
                    </Typography>
                    <Tooltip title="Binärwert kopieren">
                      <IconButton
                        size="small"
                        onClick={() => copyToClipboard(toBinary(parseInt(addressInput)), 'Binärwert')}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Mode 2: DIP Switches → Address */}
        <Card elevation={2}>
          <CardContent>
            <Stack spacing={3}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: tokens.primary }}>
                2. Dip-Schalter → Adresse
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Klicke auf die Schalter, um sie umzuschalten:
              </Typography>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'repeat(4, 1fr)',
                    sm: 'repeat(8, 1fr)'
                  },
                  gap: { xs: 1, sm: 2 },
                  maxWidth: 800,
                  mx: 'auto'
                }}
              >
                {DIP_VALUES.map((value, index) => (
                  <Box key={value} sx={{ aspectRatio: '1/1.4' }}>
                    <DipSwitchSVG
                      index={index}
                      isOn={switchStates[index]}
                      value={value}
                      interactive
                      onToggle={() => handleSwitchToggle(index)}
                      darkMode={darkMode}
                    />
                  </Box>
                ))}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 3,
                  bgcolor: tokens.primary + '15',
                  borderRadius: 2
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Berechnete Adresse:
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: (darkMode ? '#22A84A' : '#1B873B') }}>
                  {calculatedAddress}
                </Typography>
                <Tooltip title="Adresse kopieren">
                  <IconButton onClick={() => copyToClipboard(String(calculatedAddress), 'Adresse')}>
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
                <Box sx={{ flexGrow: 1 }} />
                <Button variant="outlined" onClick={resetSwitches}>
                  Alle aus
                </Button>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 1.5,
                  bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                  borderRadius: 1
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Binär:
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                  {toBinary(calculatedAddress)}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Mode 3: Potentiometers */}
        <Card elevation={2}>
          <CardContent>
            <Stack spacing={3}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: tokens.primary }}>
                3. Potentiometer-Einstellung (0-255)
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Stelle die Potentiometer ein: <strong>Poti 2</strong> zählt Sechzehner (×16), <strong>Poti 1</strong> zählt Einer (0-15)
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" sx={{ maxWidth: 600, mx: 'auto' }}>
                <TextField
                  fullWidth
                  label="Adresse eingeben (0-255)"
                  value={potiAddressInput}
                  onChange={(e) => handlePotiAddressChange(e.target.value)}
                  error={potiAddressInput.trim() !== '' && (!/^\d+$/.test(potiAddressInput.trim()) || parseInt(potiAddressInput) > 255)}
                  helperText="Gib eine Adresse ein, um die Potentiometer automatisch zu stellen."
                  inputProps={{ maxLength: 3, inputMode: 'numeric' }}
                />
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      const num = parseInt(potiAddressInput) || 0;
                      handlePotiAddressChange(String(Math.max(0, num - 1)));
                    }}
                    sx={{ minWidth: 80 }}
                  >
                    -1
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      const num = parseInt(potiAddressInput) || 0;
                      handlePotiAddressChange(String(Math.min(255, num + 1)));
                    }}
                    sx={{ minWidth: 80 }}
                  >
                    +1
                  </Button>
                </Stack>
              </Stack>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  gap: 4,
                  maxWidth: 600,
                  mx: 'auto'
                }}
              >
                <PotentiometerSVG
                  label="Poti 2: Sechzehner (×16)"
                  value={poti2}
                  max={15}
                  onChange={setPoti2}
                  darkMode={darkMode}
                />
                <PotentiometerSVG
                  label="Poti 1: Einer (0-15)"
                  value={poti1}
                  max={15}
                  onChange={setPoti1}
                  darkMode={darkMode}
                />
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  alignItems: 'center',
                  p: 3,
                  bgcolor: tokens.primary + '15',
                  borderRadius: 2
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Berechnung: ({poti2} × 16) + {poti1} =
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Typography variant="h3" sx={{ fontWeight: 800, color: (darkMode ? '#22A84A' : '#1B873B') }}>
                    {potiAddress}
                  </Typography>
                  <Tooltip title="Adresse kopieren">
                    <IconButton onClick={() => copyToClipboard(String(potiAddress), 'Adresse')}>
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Binär: <span style={{ fontFamily: 'monospace' }}>{toBinary(potiAddress)}</span>
                </Typography>
              </Box>

              {/* Quick Presets */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Schnellwahl:
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {[1, 16, 32, 64, 128, 255].map((preset) => (
                    <Button
                      key={preset}
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setPoti2(Math.floor(preset / 16));
                        setPoti1(preset % 16);
                      }}
                    >
                      {preset}
                    </Button>
                  ))}
                </Stack>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card elevation={1} sx={{ bgcolor: tokens.primary + '08' }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              💡 Anwendungsbereiche:
            </Typography>
            <Typography variant="body2" color="text.secondary" component="div">
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li>DMX-Geräte (Bühnenbeleuchtung, Theater)</li>
                <li>BACnet-Devices (Gebäudeautomation)</li>
                <li>Modbus-RTU Slave-Adressen</li>
                <li>SPS-Eingänge mit DIP-Switch-Konfiguration</li>
              </ul>
            </Typography>
          </CardContent>
        </Card>
      </Stack>

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={3500}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)}>
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </UnitPageShell>
  );
};

export default Page;


